import Swal from "sweetalert2";
import { get_api_error_message } from "./donation_campaigns_form_helper";
import {
  format_comment_time_ago,
  get_comment_profile_picture,
} from "./donation_campaigns_comments_display_helper";

function get_comment_message_errors(error) {
  const response_data = error?.responseJSON || error;
  return response_data?.message && typeof response_data.message !== "string"
    ? response_data.message
    : null;
}

function get_comment_api_error_message(error, fallback_message) {
  return get_api_error_message(error, fallback_message);
}

function get_comment_user_id(comment = {}) {
  return comment?.base_profile?.user?.id || null;
}

function replace_selected_campaign(instance, updated_campaign) {
  instance.public_campaigns = instance.public_campaigns.map((campaign) => (
    Number(campaign?.id) === Number(updated_campaign?.id) ? updated_campaign : campaign
  ));

  instance.my_campaigns_data = instance.my_campaigns_data.map((campaign) => (
    Number(campaign?.id) === Number(updated_campaign?.id) ? updated_campaign : campaign
  ));
}

export function get_campaign_comments(campaign = {}) {
  return Array.isArray(campaign?.comments) ? campaign.comments : [];
}

function refresh_campaign_comments(instance, fallback_campaign = null) {
  const requests = [instance.fetch_campaigns_api()];
  if (instance.token_exists) {
    requests.push(instance.fetch_my_campaigns_api());
  }

  return Promise.allSettled(requests).then(() => {
    const refreshed_campaign = instance.all_campaigns.find(
      (campaign) => Number(campaign?.id) === Number(instance.selected_campaign_id)
    ) || fallback_campaign;

    if (refreshed_campaign?.id) {
      replace_selected_campaign(instance, refreshed_campaign);
    }
  });
}

export const campaign_comment_methods = {
  reset_comment_form() {
    this.comment_form = { comment: "" };
    this.comment_errors = { comment: "" };
    this.is_submitting_comment = false;
    this.editing_comment_id = null;
    this.expanded_comments = {};
  },
  set_comment_error(message) {
    this.comment_errors = { comment: message || "" };
  },
  start_comment_edit(comment) {
    this.editing_comment_id = comment?.id || null;
    this.comment_form.comment = comment?.comment || comment?.message || "";
    this.set_comment_error("");
  },
  cancel_comment_edit() {
    this.editing_comment_id = null;
    this.comment_form.comment = "";
    this.set_comment_error("");
  },
  submit_campaign_comment() {
    const comment_text = String(this.comment_form.comment || "").trim();
    if (!comment_text) {
      this.set_comment_error("Comment is required.");
      return;
    }

    this.set_comment_error("");
    this.is_submitting_comment = true;

    $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/create_campaign_comment/`,
      method: "POST",
      headers: {
        ...this.get_request_headers(),
        "Content-Type": "application/json",
      },
      data: JSON.stringify({
        campaign_id: this.selected_campaign?.id,
        comment: comment_text,
      }),
      success: (response_data) => {
        const updated_campaign = response_data?.data || response_data?.campaign || null;
        refresh_campaign_comments(this, updated_campaign || this.selected_campaign).then(() => {
          this.reset_comment_form();
        });
      },
      error: (error) => {
        const message_errors = get_comment_message_errors(error);
        if (message_errors?.comment?.[0]) {
          this.set_comment_error(message_errors.comment[0]);
          return;
        }

        Swal.fire({
          icon: "error",
          title: get_comment_api_error_message(this, error, "Unable to add comment."),
          showConfirmButton: false,
          timer: 2500,
          position: "bottom-end",
          toast: true
        });
      },
      complete: () => {
        this.is_submitting_comment = false;
      },
    });
  },
  update_campaign_comment(comment) {
    const comment_text = String(this.comment_form.comment || "").trim();
    if (!comment?.id || !comment_text) {
      this.set_comment_error("Comment is required.");
      return;
    }

    this.set_comment_error("");
    this.is_submitting_comment = true;

    $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/update_campaign_comment/`,
      method: "POST",
      headers: {
        ...this.get_request_headers(),
        "Content-Type": "application/json",
      },
      data: JSON.stringify({
        comment_id: comment.id,
        comment: comment_text,
      }),
      success: (response_data) => {
        const updated_campaign = response_data?.data || response_data?.campaign || null;
        refresh_campaign_comments(this, updated_campaign || this.selected_campaign).then(() => {
          this.reset_comment_form();
        });
      },
      error: (error) => {
        const message_errors = get_comment_message_errors(error);
        if (message_errors?.comment?.[0]) {
          this.set_comment_error(message_errors.comment[0]);
          return;
        }

        Swal.fire({
          icon: "error",
          title: get_comment_api_error_message(this, error, "Unable to update comment."),
          showConfirmButton: false,
          timer: 2500,
          position: "bottom-end",
          toast: true
        });
      },
      complete: () => {
        this.is_submitting_comment = false;
      },
    });
  },
  save_campaign_comment() {
    if (this.editing_comment_id) {
      const comment = this.selected_campaign_comments.find(
        (entry) => Number(entry?.id) === Number(this.editing_comment_id)
      );
      this.update_campaign_comment(comment);
      return;
    }

    this.submit_campaign_comment();
  },
  delete_campaign_comment(comment) {
    if (!comment?.id) {
      return;
    }

    $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/delete_campaign_comment/`,
      method: "DELETE",
      headers: {
        ...this.get_request_headers(),
        "Content-Type": "application/json",
      },
      data: JSON.stringify({ comment_id: comment.id }),
      success: (response_data) => {
        const updated_campaign = response_data?.data || response_data?.campaign || null;
        refresh_campaign_comments(this, updated_campaign || this.selected_campaign).then(() => {
          if (Number(this.editing_comment_id) === Number(comment.id)) {
            this.reset_comment_form();
          }
        });
      },
      error: (error) => {
        Swal.fire({
          icon: "error",
          title: get_comment_api_error_message(this, error, "Unable to delete comment."),
          showConfirmButton: false,
          timer: 2500,
          position: "bottom-end",
          toast: true
        });
      },
    });
  },
  can_manage_comment(comment) {
    const comment_user_id = get_comment_user_id(comment);
    const current_user_id = this.user_details?.user_id;
    const is_admin = String(this.user_details?.user_role || "").toLowerCase() === "admin";

    return is_admin || Number(comment_user_id) === Number(current_user_id);
  },
  should_truncate_comment(text) {
    return String(text || "").trim().length > 140;
  },
  get_truncated_comment(text, limit = 140) {
    const normalized_text = String(text || "").trim();
    if (normalized_text.length <= limit) {
      return normalized_text;
    }

    return `${normalized_text.slice(0, limit).trimEnd()}...`;
  },
  is_comment_expanded(comment_id) {
    return Boolean(this.expanded_comments?.[String(comment_id || "")]);
  },
  toggle_comment_expanded(comment_id) {
    const key = String(comment_id || "");
    this.expanded_comments = {
      ...(this.expanded_comments || {}),
      [key]: !this.is_comment_expanded(comment_id),
    };
  },
  format_comment_time_ago,
  get_comment_profile_picture,
};
